import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Building, Users, Shield, TrendingUp, Clock, CheckCircle, Phone, Mail } from 'lucide-react';
import { getSaveaPhone, getSaveaPhoneTel } from '../config/phoneConfig';

export const MethodologyPage = () => {
  const { i18n } = useTranslation();
  const isSpanish = i18n.language === 'es';
  const country = isSpanish ? 'ES' : 'FR';
  const saveaPhone = getSaveaPhone(country, 'electricity', 'LP');

  return (
    <div className="min-h-screen bg-gradient-to-br from-sage-50 to-white">
      <section className="py-16 lg:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-4xl lg:text-5xl font-bold text-sage-900 mb-4">
              {isSpanish ? 'Metodología de Clasificación' : 'Méthodologie de Classement'}
            </h1>
            <p className="text-xl text-sage-600">
              {isSpanish 
                ? 'Cómo funciona SAVEA y nuestros servicios de comparación energética'
                : 'Comment fonctionne SAVEA et nos services de comparaison énergétique'
              }
            </p>
            <div className="mt-4 text-sm text-sage-500">
              {isSpanish ? 'Última modificación: 01 Enero 2026' : 'Dernière modification : 01 Janvier 2026'}
            </div>
          </div>

          {/* Qui sommes-nous */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building className="w-5 h-5 text-sage-600" />
                {isSpanish ? '¿Quiénes somos?' : 'Qui sommes-nous ?'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sage-700 leading-relaxed">
                {isSpanish 
                  ? 'SAVEA es una plataforma digital operada por SOLUCIONES INTEGRALES HORIZON, S.L.U. que tiene como objetivo ayudar a particulares a optimizar sus gastos domésticos, especialmente en energía e internet. Ofrecemos herramientas gratuitas de comparación y ponemos en contacto a usuarios con especialistas para encontrar las mejores ofertas disponibles.'
                  : 'SAVEA est une plateforme numérique exploitée par SOLUCIONES INTEGRALES HORIZON, S.L.U. qui a pour objectif d\'aider les particuliers à optimiser leurs dépenses domestiques, notamment en énergie et internet. Nous proposons des outils gratuits de comparaison et mettons en relation les utilisateurs avec des spécialistes pour trouver les meilleures offres disponibles.'
                }
              </p>
              <p className="text-sage-700 leading-relaxed">
                {isSpanish
                  ? 'Después de analizar las necesidades del usuario, nuestros especialistas proponen las ofertas más adaptadas a su perfil de consumo. Una vez elegida la oferta, acompañamos al usuario durante todo el proceso.'
                  : 'Après avoir analysé les besoins de l\'utilisateur, nos spécialistes proposent les offres les plus adaptées à son profil de consommation. Une fois l\'offre choisie, nous accompagnons l\'utilisateur durant tout le processus.'
                }
              </p>
            </CardContent>
          </Card>

          {/* Comment fonctionne notre service */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-sage-600" />
                {isSpanish ? '¿Cómo funciona nuestro servicio?' : 'Comment fonctionne notre service ?'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sage-700 leading-relaxed">
                {isSpanish
                  ? 'Nuestro servicio es totalmente gratuito para los usuarios. Nuestro modelo económico se basa en las comisiones que recibimos de algunos proveedores cuando facilitamos el contacto con clientes potenciales. El consumidor se beneficia de nuestro servicio sin pagar ninguna tarifa adicional.'
                  : 'Notre service est totalement gratuit pour les utilisateurs. Notre modèle économique repose sur les commissions que nous percevons de certains fournisseurs lorsque nous facilitons la mise en relation avec des clients potentiels. Le consommateur bénéficie de notre service sans payer aucun frais supplémentaire.'
                }
              </p>

              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h4 className="font-semibold text-green-800 mb-2">
                  {isSpanish ? 'Nuestros Especialistas' : 'Nos Spécialistes'}
                </h4>
                <p className="text-green-700 text-sm mb-3">
                  {isSpanish
                    ? 'Nuestros especialistas SAVEA están formados en contratos de energía e internet para Francia y España. Te ayudan gratuitamente a comparar ofertas y encontrar la mejor opción según tu consumo.'
                    : 'Nos spécialistes SAVEA sont formés aux contrats d\'énergie et internet pour la France et l\'Espagne. Ils vous aident gratuitement à comparer les offres et trouver la meilleure option selon votre consommation.'
                  }
                </p>
                <Button 
                  onClick={() => window.open(`tel:${getSaveaPhoneTel(country, 'electricity', 'LP')}`)}
                  size="sm"
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  <Phone className="w-4 h-4 mr-2" />
                  {isSpanish ? 'Contactar Especialista' : 'Contacter Spécialiste'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Méthodologie de classement */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-sage-600" />
                {isSpanish ? 'Metodología de Clasificación' : 'Méthodologie de Classement'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sage-700 leading-relaxed">
                {isSpanish
                  ? 'En nuestros comparadores, clasificamos las ofertas de proveedores por precio creciente por defecto. Sin embargo, nos reservamos la posibilidad de destacar ciertas ofertas según criterios específicos como ofertas verdes, las más económicas o las más populares.'
                  : 'Dans nos comparateurs, nous classons les offres des fournisseurs par prix croissant par défaut. Cependant, nous nous réservons la possibilité de mettre en avant certaines offres selon des critères spécifiques comme les offres vertes, les moins chères ou les plus populaires.'
                }
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-sage-50 rounded-lg border border-sage-200">
                  <h4 className="font-semibold text-sage-800 mb-2">
                    {isSpanish ? 'Criterios de Clasificación' : 'Critères de Classement'}
                  </h4>
                  <ul className="space-y-1 text-sm text-sage-600">
                    <li>• {isSpanish ? 'Precio por kWh (principal)' : 'Prix par kWh (principal)'}</li>
                    <li>• {isSpanish ? 'Coste de suscripción mensual' : 'Coût d\'abonnement mensuel'}</li>
                    <li>• {isSpanish ? 'Servicios incluidos' : 'Services inclus'}</li>
                    <li>• {isSpanish ? 'Calidad del servicio' : 'Qualité de service'}</li>
                  </ul>
                </div>

                <div className="p-4 bg-sage-50 rounded-lg border border-sage-200">
                  <h4 className="font-semibold text-sage-800 mb-2">
                    {isSpanish ? 'Fuentes de Información' : 'Sources d\'Information'}
                  </h4>
                  <ul className="space-y-1 text-sm text-sage-600">
                    <li>• {isSpanish ? 'Sitios web oficiales de proveedores' : 'Sites web officiels des fournisseurs'}</li>
                    <li>• {isSpanish ? 'Tarifarios públicos' : 'Grilles tarifaires publiques'}</li>
                    <li>• {isSpanish ? 'Información regulatoria oficial' : 'Informations réglementaires officielles'}</li>
                    <li>• {isSpanish ? 'Actualización mensual mínima' : 'Mise à jour mensuelle minimum'}</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Partenariats via plateformes d'affiliation */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-sage-600" />
                {isSpanish ? 'Colaboraciones con Proveedores' : 'Collaborations avec les Fournisseurs'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sage-700 leading-relaxed">
                {isSpanish
                  ? 'SAVEA facilita la puesta en contacto entre usuarios y proveedores. Podemos percibir comisiones de algunos proveedores sin que esto afecte el precio final para el consumidor.'
                  : 'SAVEA facilite la mise en relation entre utilisateurs et fournisseurs. Nous pouvons percevoir des commissions de certains fournisseurs sans que cela affecte le prix final pour le consommateur.'
                }
              </p>
              
              <p className="text-sage-700 leading-relaxed">
                {isSpanish
                  ? 'Nuestros contratos comerciales se obtienen a través de plataformas de afiliación reconocidas del mercado.'
                  : 'Nos contrats commerciaux sont obtenus au travers de plateformes d\'affiliation reconnues du marché.'
                }
              </p>
            </CardContent>
          </Card>

          {/* Actualización de precios */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-sage-600" />
                {isSpanish ? 'Actualización de Precios' : 'Mise à Jour des Prix'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sage-700 leading-relaxed">
                {isSpanish
                  ? 'Los precios y ofertas mencionados se actualizan regularmente, como mínimo mensualmente, según la evolución de las tarifas de cada proveedor. Para ofertas de energía, nuestros comparadores se actualizan cada mes según las ofertas disponibles.'
                  : 'Les prix et offres mentionnés sont mis à jour régulièrement, au minimum mensuellement, selon l\'évolution des tarifs de chaque fournisseur. Pour les offres d\'énergie, nos comparateurs sont mis à jour chaque mois selon les offres disponibles.'
                }
              </p>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h4 className="font-semibold text-yellow-800 mb-2">
                  {isSpanish ? 'Aviso Importante' : 'Avertissement Important'}
                </h4>
                <p className="text-yellow-700 text-sm">
                  {isSpanish
                    ? 'Los precios mostrados son estimaciones basadas en datos públicos. Las condiciones exactas pueden variar. Para información definitiva, contacte directamente a los proveedores o a nuestros especialistas.'
                    : 'Les prix affichés sont des estimations basées sur des données publiques. Les conditions exactes peuvent varier. Pour des informations définitives, contactez directement les fournisseurs ou nos spécialistes.'
                  }
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Modèle économique */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-sage-600" />
                {isSpanish ? 'Modelo Económico' : 'Modèle Économique'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sage-700 leading-relaxed">
                {isSpanish
                  ? 'SAVEA opera con un modelo freemium: nuestras herramientas básicas son gratuitas para maximizar el acceso a información energética de calidad. Podemos recibir comisiones de algunos comparadores cuando facilitamos la puesta en contacto con especialistas, sin que esto afecte nunca el precio final para el consumidor.'
                  : 'SAVEA fonctionne avec un modèle freemium : nos outils de base sont gratuits pour maximiser l\'accès à une information énergétique de qualité. Nous pouvons percevoir des commissions de certains comparateurs lorsque nous facilitons la mise en relation avec des spécialistes, sans que cela n\'affecte jamais le prix final pour le consommateur.'
                }
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-1" />
                  <div>
                    <div className="font-medium text-sage-800 text-sm">
                      {isSpanish ? 'Herramientas Siempre Gratuitas' : 'Outils Toujours Gratuits'}
                    </div>
                    <div className="text-sage-600 text-xs">
                      {isSpanish ? 'Simuladores, calculadoras, plantillas' : 'Simulateurs, calculateurs, templates'}
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-1" />
                  <div>
                    <div className="font-medium text-sage-800 text-sm">
                      {isSpanish ? 'Especialistas Gratuitos' : 'Spécialistes Gratuits'}
                    </div>
                    <div className="text-sage-600 text-xs">
                      {isSpanish ? 'Asesoramiento energético sin coste' : 'Conseil énergétique sans coût'}
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Contact et informations légales */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building className="w-5 h-5 text-sage-600" />
                {isSpanish ? 'Información Legal y Contacto' : 'Informations Légales et Contact'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-sage-800 mb-2">
                    {isSpanish ? 'Empresa' : 'Entreprise'}
                  </h4>
                  <p className="text-sage-600 text-sm">
                    SOLUCIONES INTEGRALES HORIZON, S.L.U.<br />
                    NIF: B23972946<br />
                    Calle Radas 57, Ático 1<br />
                    08004 Barcelona, España<br />
                    <span className="mt-1 block">{isSpanish ? 'Representante Legal' : 'Représentant Légal'}: Julien Mercier (NIE: Y9783769C)</span>
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-sage-800 mb-2">
                    {isSpanish ? 'Contacto' : 'Contact'}
                  </h4>
                  <div className="space-y-1 text-sm text-sage-600">
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      <a href="mailto:info@savea.info" className="hover:text-sage-800">
                        info@savea.info
                      </a>
                    </div>
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4" />
                      <span>{saveaPhone}</span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};